import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showError, showSuccess } from '../../services/alert';
import { useNavigate, useParams } from 'react-router-dom';

const novoItem = () => ({
  estoqueAgricolaId: '',
  descricao: '',
  ncm: '',
  cfop: '',
  unidade: 'UN',
  quantidade: 1,
  valorUnitario: 0,
  valorDesconto: 0,
  cstIcms: '',
  cstPis: '',
  cstCofins: '',
  aliqIcms: 0,
  aliqPis: 0,
  aliqCofins: 0
});

const onlyDigits = (v) => (v || '').toString().replace(/\D/g, '');

const NotaFiscalForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    agricultorId: '',
    tipoDestinatario: 1,
    destinatarioId: '',
    valorProdutos: 0,
    valorDesconto: 0,
    valorFrete: 0,
    valorTotal: 0,
    ambiente: 'Producao',

    // ===== NOVO: frete / transporte =====
    modFrete: 9,               // 9 = sem frete
    transportadoraId: '',
    placaVeiculo: '',
    ufPlaca: '',
    rntc: '',

    itens: [novoItem()]
  });

  const [agricultores, setAgricultores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [produtos, setProdutos] = useState([]);

  // ===== NOVO: lista transportadoras =====
  const [transportadoras, setTransportadoras] = useState([]);

  useEffect(() => {
    carregarListas();
    if (id && id !== 'nova') carregarNota(id);
  }, [id]);

  const carregarListas = async () => {
    try {
      const [ag, cl, fo, tr, pr] = await Promise.all([
        api.get('/agricultores'),
        api.get('/cliente'),
        api.get('/fornecedor'),
        api.get('/transportadoras'),
        api.get('/producao-agricola/estoques')
      ]);

      setAgricultores(Array.isArray(ag.data) ? ag.data : []);
      setClientes(Array.isArray(cl.data) ? cl.data : []);
      setFornecedores(Array.isArray(fo.data) ? fo.data : []);
      setTransportadoras(Array.isArray(tr.data) ? tr.data : []);
      setProdutos(Array.isArray(pr.data) ? pr.data : []);
    } catch (err) {
      console.error('Erro ao carregar listas de apoio:', err);
      showError('Erro ao carregar listas de apoio.');
      setAgricultores([]);
      setClientes([]);
      setFornecedores([]);
      setTransportadoras([]);
    }
  };

  const carregarNota = async (idNota) => {
    try {
      const { data } = await api.get(`/notafiscal/${idNota}`);

      setForm((prev) => ({
        ...prev,
        ...data,

        // ✅ garante defaults caso o backend não traga ainda
        modFrete: data?.modFrete ?? prev.modFrete ?? 9,
        transportadoraId: data?.transportadoraId ?? prev.transportadoraId ?? '',
        placaVeiculo: data?.placaVeiculo ?? prev.placaVeiculo ?? '',
        ufPlaca: data?.ufPlaca ?? prev.ufPlaca ?? '',
        rntc: data?.rntc ?? prev.rntc ?? '',

        itens: Array.isArray(data.itens) && data.itens.length > 0 ? data.itens : prev.itens
      }));
    } catch {
      showError('Erro ao carregar nota fiscal.');
    }
  };

  const destinatarios = form.tipoDestinatario === 1 ? clientes : fornecedores;

  // ====== cálculos de totais ======
  const resumo = useMemo(() => {
    const itens = Array.isArray(form.itens) ? form.itens : [];
    const totalProdutos = itens.reduce((acc, it) => {
      const qtd = Number(it.quantidade || 0);
      const vUn = Number(it.valorUnitario || 0);
      return acc + qtd * vUn;
    }, 0);

    const totalDesconto = itens.reduce((acc, it) => acc + Number(it.valorDesconto || 0), 0);
    const frete = Number(form.valorFrete || 0);

    let total = (totalProdutos - totalDesconto) + frete;
    if (total < 0) total = 0;

    return {
      totalProdutos,
      totalDesconto,
      total
    };
  }, [form.itens, form.valorFrete]);

  // aplica totais automaticamente no form
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      valorProdutos: Number(resumo.totalProdutos.toFixed(2)),
      valorDesconto: Number(resumo.totalDesconto.toFixed(2)),
      valorTotal: Number(resumo.total.toFixed(2))
    }));
  }, [resumo.totalProdutos, resumo.totalDesconto, resumo.total]);

  // ====== handlers de itens ======
  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const itens = [...(prev.itens || [])];
      const atual = { ...(itens[index] || novoItem()) };

      if (field === 'ncm') value = onlyDigits(value).slice(0, 8);
      if (field === 'cfop') value = onlyDigits(value).slice(0, 4);

      if (['quantidade', 'valorUnitario', 'valorDesconto', 'aliqIcms', 'aliqPis', 'aliqCofins'].includes(field)) {
        value = value === '' ? '' : Number(value);
      }

      atual[field] = value;
      itens[index] = atual;

      return { ...prev, itens };
    });
  };

  const selecionarProduto = (index, produtoId) => {
    const produto = produtos.find(p => String(p.id) === String(produtoId));
    setForm(prev => ({ ...prev, itens: prev.itens.map((item, i) => i !== index ? item : ({ ...item,
      estoqueAgricolaId: produtoId, descricao: produto?.produto || '', unidade: produto?.unidade || 'UN',
      ncm: produto?.configuracaoFiscal?.ncm || '', cfop: produto?.configuracaoFiscal?.cfopPadrao || '',
      cstIcms: produto?.configuracaoFiscal?.csosn || produto?.configuracaoFiscal?.cstIcms || '',
      cstPis: produto?.configuracaoFiscal?.cstPis || '', cstCofins: produto?.configuracaoFiscal?.cstCofins || ''
    })) }));
  };

  const adicionarItem = () => {
    setForm((prev) => ({ ...prev, itens: [...(prev.itens || []), novoItem()] }));
  };

  const removerItem = (index) => {
    setForm((prev) => {
      const itens = [...(prev.itens || [])];
      itens.splice(index, 1);
      return { ...prev, itens: itens.length > 0 ? itens : [novoItem()] };
    });
  };

  // ===== NOVO: validação frete/transporte (mínimo viável) =====
  const validarTransporte = () => {
    const mod = Number(form.modFrete ?? 9);

    // 9 = sem frete → não exige nada
    if (mod === 9) return null;

    // se não for "sem frete", pelo menos precisa estar coerente
    // (mínimo viável: exige transportadora quando for 0/1/2)
    if ([0, 1, 2].includes(mod) && !form.transportadoraId) {
      return 'Selecione a Transportadora (ou use "9 - Sem frete").';
    }

    // placa sem UF ou UF sem placa
    const placa = (form.placaVeiculo || '').trim();
    const uf = (form.ufPlaca || '').trim();
    if ((placa && !uf) || (!placa && uf)) {
      return 'Informe a UF da placa junto com a placa (ou deixe ambos em branco).';
    }

    if (uf && uf.length !== 2) {
      return 'UF da placa deve ter 2 letras (ex: MT).';
    }

    return null;
  };

  const validarAntesEmitir = () => {
    if (!form.agricultorId) return 'Selecione o Agricultor (Emitente).';
    if (!form.destinatarioId) return 'Selecione o Destinatário.';
    if (!form.itens || form.itens.length === 0) return 'Informe ao menos 1 item.';

    // ✅ novo
    const erroTransp = validarTransporte();
    if (erroTransp) return erroTransp;

    for (let i = 0; i < form.itens.length; i++) {
      const it = form.itens[i];
      const idx = i + 1;

      if (!it.estoqueAgricolaId) return `Item ${idx}: selecione um produto cadastrado.`;
      if (!it.ncm || onlyDigits(it.ncm).length !== 8) return `Item ${idx}: NCM deve ter 8 dígitos.`;
      if (!it.cfop || onlyDigits(it.cfop).length !== 4) return `Item ${idx}: CFOP deve ter 4 dígitos.`;
      if (!it.unidade) return `Item ${idx}: informe a unidade (ex: UN, KG).`;

      const qtd = Number(it.quantidade || 0);
      const vUn = Number(it.valorUnitario || 0);
      if (qtd <= 0) return `Item ${idx}: quantidade deve ser maior que 0.`;
      if (vUn <= 0) return `Item ${idx}: valor unitário deve ser maior que 0.`;
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const erro = validarAntesEmitir();
    if (erro) {
      showError(erro);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        agricultorId: Number(form.agricultorId),
        tipoDestinatario: Number(form.tipoDestinatario),
        destinatarioId: Number(form.destinatarioId),
        valorProdutos: Number(form.valorProdutos || 0),
        valorDesconto: Number(form.valorDesconto || 0),
        valorFrete: Number(form.valorFrete || 0),
        valorTotal: Number(form.valorTotal || 0),
        ambiente: form.ambiente,

        // ✅ NOVO: transporte
        modFrete: Number(form.modFrete ?? 9),
        transportadoraId: form.transportadoraId ? Number(form.transportadoraId) : null,
        placaVeiculo: (form.placaVeiculo || '').trim() ? form.placaVeiculo.trim().toUpperCase() : null,
        ufPlaca: (form.ufPlaca || '').trim() ? form.ufPlaca.trim().toUpperCase() : null,
        rntc: (form.rntc || '').trim() ? form.rntc.trim() : null,

        itens: (form.itens || []).map((it) => ({
          estoqueAgricolaId: Number(it.estoqueAgricolaId),
          descricao: it.descricao,
          ncm: onlyDigits(it.ncm),
          cfop: onlyDigits(it.cfop),
          unidade: it.unidade || 'UN',
          quantidade: Number(it.quantidade || 0),
          valorUnitario: Number(it.valorUnitario || 0),
          valorDesconto: Number(it.valorDesconto || 0),
          cstIcms: it.cstIcms || '',
          cstPis: it.cstPis || '',
          cstCofins: it.cstCofins || '',
          aliqIcms: it.aliqIcms === '' ? null : Number(it.aliqIcms || 0),
          aliqPis: it.aliqPis === '' ? null : Number(it.aliqPis || 0),
          aliqCofins: it.aliqCofins === '' ? null : Number(it.aliqCofins || 0)
        }))
      };

      await api.post('/notafiscal/emitir', payload);
      showSuccess('Nota fiscal emitida com sucesso!');
      navigate('/notas-fiscais');
    } catch (err) {
      const data = err?.response?.data;

const msg =
  data?.Message ||      // <- seu caso atual
  data?.message ||      // <- padrão camelCase
  data?.title ||
  data?.detail ||
  data ||
  'Erro ao emitir nota fiscal.';

showError(typeof msg === 'string' ? msg : 'Erro ao emitir nota fiscal.');
    } finally {
      setLoading(false);
    }
  };

  const modFreteAtual = Number(form.modFrete ?? 9);
  const habilitaTransporte = modFreteAtual !== 9;

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-light btn-sm d-flex align-items-center gap-1"
              onClick={() => navigate('/notas-fiscais')}
            >
              <i className="bi bi-arrow-left text-success"></i>
              <span className="text-success fw-semibold">Voltar</span>
            </button>
            <div>
              <h5 className="mb-0">{id === 'nova' ? 'Nova Nota Fiscal' : 'Editar Nota Fiscal'}</h5>
              <small className="text-white-50">Preencha os dados e emita a NF-e</small>
            </div>
          </div>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            {/* ===== Cabeçalho ===== */}
            <div className="col-md-4">
              <label className="form-label">Agricultor (Emitente)</label>
              <select
                className="form-select"
                value={form.agricultorId}
                onChange={(e) => setForm({ ...form, agricultorId: e.target.value })}
                required
              >
                <option value="">Selecione...</option>
                {agricultores.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nome}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                * O emitente precisa estar com endereço, cMun (IBGE) e CRT completos no cadastro.
              </small>
            </div>

            <div className="col-md-4">
              <label className="form-label">Tipo de Destinatário</label>
              <select
                className="form-select"
                value={form.tipoDestinatario}
                onChange={(e) =>
                  setForm({ ...form, tipoDestinatario: +e.target.value, destinatarioId: '' })
                }
              >
                <option value="1">Cliente</option>
                <option value="2">Fornecedor</option>
              </select>
            </div>

            <div className="col-md-4">
              <label className="form-label">Destinatário</label>
              <select
                className="form-select"
                value={form.destinatarioId}
                onChange={(e) => setForm({ ...form, destinatarioId: e.target.value })}
                required
              >
                <option value="">Selecione...</option>
                {destinatarios.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.nome} - {d.documento}
                  </option>
                ))}
              </select>
              <small className="text-muted">
                * Cliente/Fornecedor precisa estar com endereço completo + cMun (IBGE).
              </small>
            </div>
,

            <div className="col-md-3">
              <label className="form-label">Frete (R$)</label>
              <CurrencyInput
                className="form-control"
                value={form.valorFrete}
                onValueChange={(value) => setForm({ ...form, valorFrete: value === '' ? '' : Number(value) })}
                placeholder="0,00"
              />
            </div>

            <div className="col-md-2">
              <label className="form-label">Produtos</label>
              <CurrencyInput className="form-control" value={form.valorProdutos} readOnly />
            </div>

            <div className="col-md-2">
              <label className="form-label">Desconto</label>
              <CurrencyInput className="form-control" value={form.valorDesconto} readOnly />
            </div>

            <div className="col-md-2">
              <label className="form-label">Total</label>
              <CurrencyInput className="form-control fw-bold" value={form.valorTotal} readOnly />
            </div>

            {/* ===== NOVO: Frete / Transporte ===== */}
            <div className="col-12 mt-2">
              <div className="card shadow-sm">
                <div className="card-body">
                  <h6 className="mb-3 text-success fw-semibold">Frete / Transporte</h6>

                  <div className="row g-3">
                    <div className="col-md-3">
                      <label className="form-label">Modalidade do Frete</label>
                      <select
                        className="form-select"
                        value={form.modFrete ?? 9}
                        onChange={(e) => {
                          const v = parseInt(e.target.value, 10);
                          setForm((prev) => ({
                            ...prev,
                            modFrete: v,
                            ...(v === 9
                              ? { transportadoraId: '', placaVeiculo: '', ufPlaca: '', rntc: '' }
                              : {})
                          }));
                        }}
                      >
                        <option value={0}>0 - Por conta do emitente</option>
                        <option value={1}>1 - Por conta do destinatário</option>
                        <option value={2}>2 - Por conta de terceiros</option>
                        <option value={3}>3 - Próprio por conta do remetente</option>
                        <option value={4}>4 - Próprio por conta do destinatário</option>
                        <option value={9}>9 - Sem frete</option>
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Transportadora</label>
                      <select
                        className="form-select"
                        value={form.transportadoraId || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, transportadoraId: e.target.value }))}
                        disabled={!habilitaTransporte}
                      >
                        <option value="">Selecione...</option>
                        {transportadoras.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.nome} {t.documento ? `- ${t.documento}` : ''}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">
                        * Use quando houver transportadora envolvida.
                      </small>
                    </div>

                    <div className="col-md-3">
                      <label className="form-label">Placa do Veículo</label>
                      <input
                        className="form-control"
                        value={form.placaVeiculo || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, placaVeiculo: e.target.value.toUpperCase() }))}
                        placeholder="AAA0A00 / AAA0000"
                        disabled={!habilitaTransporte}
                        maxLength={8}
                      />
                    </div>

                    <div className="col-md-2">
                      <label className="form-label">UF Placa</label>
                      <input
                        className="form-control"
                        value={form.ufPlaca || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, ufPlaca: e.target.value.toUpperCase() }))}
                        placeholder="MT"
                        disabled={!habilitaTransporte}
                        maxLength={2}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">RNTC</label>
                      <input
                        className="form-control"
                        value={form.rntc || ''}
                        onChange={(e) => setForm((prev) => ({ ...prev, rntc: e.target.value }))}
                        placeholder="(opcional)"
                        disabled={!habilitaTransporte}
                        maxLength={20}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Itens ===== */}
            <div className="col-12 mt-3">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 text-success fw-semibold">Itens da NF-e</h6>
                <button type="button" className="btn btn-outline-success btn-sm" onClick={adicionarItem}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Adicionar item
                </button>
              </div>

              <div className="table-responsive mt-2">
                {(form.itens||[]).some(it => it.estoqueAgricolaId && !produtos.find(p => String(p.id)===String(it.estoqueAgricolaId))?.configuracaoFiscalCompleta) && <div className="alert alert-warning d-flex justify-content-between align-items-center"><span><strong>Existem produtos com configuração fiscal incompleta.</strong> Regularize antes de emitir.</span><button type="button" className="btn btn-warning btn-sm" onClick={()=>{const item=(form.itens||[]).find(it=>it.estoqueAgricolaId&&!produtos.find(p=>String(p.id)===String(it.estoqueAgricolaId))?.configuracaoFiscalCompleta);if(item)navigate(`/producao-agricola/produtos/${item.estoqueAgricolaId}/fiscal`);}}>Configurar produto</button></div>}
                <table className="table table-sm table-bordered align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 280 }}>Descrição</th>
                      <th style={{ width: 90 }}>NCM</th>
                      <th style={{ width: 80 }}>CFOP</th>
                      <th style={{ width: 70 }}>Unid.</th>
                      <th style={{ width: 90 }}>Qtd</th>
                      <th style={{ width: 120 }}>V. Unit</th>
                      <th style={{ width: 120 }}>Desc.</th>
                      <th style={{ width: 110 }}>CST/CSOSN ICMS</th>
                      <th style={{ width: 90 }}>Aliq ICMS</th>
                      <th style={{ width: 70 }}>CST PIS</th>
                      <th style={{ width: 90 }}>Aliq PIS</th>
                      <th style={{ width: 80 }}>CST COFINS</th>
                      <th style={{ width: 95 }}>Aliq COFINS</th>
                      <th style={{ width: 80 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(form.itens || []).map((it, idx) => (
                      <tr key={idx}>
                        <td>
                          <select className="form-select form-select-sm mb-1" value={it.estoqueAgricolaId || ''} onChange={e=>selecionarProduto(idx,e.target.value)} required>
                            <option value="">Selecione o produto...</option>{produtos.map(p=><option value={p.id} key={p.id}>{p.produto}{p.configuracaoFiscalCompleta?'':' — fiscal incompleto'}</option>)}
                          </select>
                          <input
                            className="form-control form-control-sm"
                            value={it.descricao}
                            onChange={(e) => handleItemChange(idx, 'descricao', e.target.value)}
                            placeholder="Ex.: SOJA EM GRÃOS"
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={it.ncm}
                            onChange={(e) => handleItemChange(idx, 'ncm', e.target.value)}
                            placeholder="8 díg."
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={it.cfop}
                            onChange={(e) => handleItemChange(idx, 'cfop', e.target.value)}
                            placeholder="4 díg."
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={it.unidade}
                            onChange={(e) => handleItemChange(idx, 'unidade', e.target.value.toUpperCase())}
                            placeholder="UN"
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.0001"
                            className="form-control form-control-sm"
                            value={it.quantidade}
                            onChange={(e) => handleItemChange(idx, 'quantidade', e.target.value)}
                          />
                        </td>
                        <td>
                          <CurrencyInput
                            className="form-control form-control-sm"
                            value={it.valorUnitario}
                            onValueChange={(value) => handleItemChange(idx, 'valorUnitario', value)}
                            placeholder="0,00"
                          />
                        </td>
                        <td>
                          <CurrencyInput
                            className="form-control form-control-sm"
                            value={it.valorDesconto}
                            onValueChange={(value) => handleItemChange(idx, 'valorDesconto', value)}
                            placeholder="0,00"
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={it.cstIcms}
                            onChange={(e) => handleItemChange(idx, 'cstIcms', e.target.value)}
                            placeholder="SN:102 / RN:00"
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm"
                            value={it.aliqIcms}
                            onChange={(e) => handleItemChange(idx, 'aliqIcms', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={it.cstPis}
                            onChange={(e) => handleItemChange(idx, 'cstPis', e.target.value)}
                            placeholder="99"
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm"
                            value={it.aliqPis}
                            onChange={(e) => handleItemChange(idx, 'aliqPis', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={it.cstCofins}
                            onChange={(e) => handleItemChange(idx, 'cstCofins', e.target.value)}
                            placeholder="99"
                            readOnly
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control form-control-sm"
                            value={it.aliqCofins}
                            onChange={(e) => handleItemChange(idx, 'aliqCofins', e.target.value)}
                          />
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removerItem(idx)}
                            title="Remover item"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <small className="text-muted">
                  Os dados fiscais são carregados automaticamente do cadastro do produto e preservados na nota emitida.
                </small>
              </div>
            </div>

            {/* ===== Botão ===== */}
            <div className="col-12 d-flex justify-content-end mt-3">
              <button type="submit" className="btn btn-success" disabled={loading}>
                <i className="bi bi-send-check me-2"></i>
                {loading ? 'Emitindo...' : 'Emitir Nota'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NotaFiscalForm;
